import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import * as usersAPI from '@/api/users'
import { getApiErrorMessage } from '@/utils/apiErrors'

const usersAdapter = createEntityAdapter({
  selectId: (user) => user.id,
  sortComparer: (a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '')
})

const initialState = usersAdapter.getInitialState({
  loading: false,
  error: null,
  filters: {
    page: 1,
  },
  pagination: null,
  pendingInvitations: [],
  pendingInvitationsLoading: false,
})

/**
 * Fetch users with optional filters
 */
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (filters = {}) => {
    return await usersAPI.getUsers(filters)
  }
)

/**
 * Invite a new user
 */
export const inviteUser = createAsyncThunk(
  'users/inviteUser',
  async (data) => {
    return await usersAPI.inviteUser(data)
  }
)

/**
 * Update user role
 */
export const updateUserRole = createAsyncThunk(
  'users/updateUserRole',
  async ({ userId, role }) => {
    return await usersAPI.updateUserRole(userId, role)
  }
)

/**
 * Assign campuses to user
 */
export const assignCampuses = createAsyncThunk(
  'users/assignCampuses',
  async ({ userId, campusIds }) => {
    return await usersAPI.assignCampuses(userId, campusIds)
  }
)

/**
 * Activate user account
 */
export const activateUser = createAsyncThunk(
  'users/activateUser',
  async (userId) => {
    return await usersAPI.activateUser(userId)
  }
)

/**
 * Deactivate user account
 */
export const deactivateUser = createAsyncThunk(
  'users/deactivateUser',
  async (userId, { rejectWithValue }) => {
    try {
      return await usersAPI.deactivateUser(userId)
    } catch (error) {
      // The API's own reason ("Cannot deactivate yourself"), not axios's text.
      return rejectWithValue(getApiErrorMessage(error, 'Failed to deactivate user'))
    }
  }
)

/**
 * Remove a member from this workspace (owner only). Resolves with the id.
 */
export const removeUser = createAsyncThunk(
  'users/removeUser',
  async (userId, { rejectWithValue }) => {
    try {
      await usersAPI.removeUser(userId)
      return userId
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to remove user'))
    }
  }
)

/**
 * Fetch pending invitations
 */
export const fetchPendingInvitations = createAsyncThunk(
  'users/fetchPendingInvitations',
  async () => {
    return await usersAPI.getPendingInvitations()
  }
)

/**
 * Cancel a pending invitation
 */
export const cancelInvitation = createAsyncThunk(
  'users/cancelInvitation',
  async (invitationId) => {
    await usersAPI.cancelInvitation(invitationId)
    return invitationId
  }
)

/**
 * Resend a pending invitation
 */
export const resendInvitation = createAsyncThunk(
  'users/resendInvitation',
  async (invitationId) => {
    return await usersAPI.resendInvitation(invitationId)
  }
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    /**
     * Set filters and trigger refetch
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload, page: 1 }
    },
    /**
     * Clear all filters
     */
    clearFilters: (state) => {
      state.filters = { page: 1 }
    },
    /**
     * Set current page
     */
    setPage: (state, action) => {
      state.filters.page = action.payload
    },
    /**
     * Clear error state
     */
    clearError: (state) => {
      state.error = null
    },
    /**
     * Upsert multiple users
     */
    upsertMany: (state, action) => {
      usersAdapter.upsertMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        usersAdapter.setAll(state, action.payload.users || [])
        state.pagination = action.payload.meta?.pagination || null
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load users'
      })

      // Invite user - refetch list after success
      .addCase(inviteUser.pending, (state) => {
        state.error = null
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to invite user'
      })

      // Update user role - update in-place
      .addCase(updateUserRole.fulfilled, (state, action) => {
        usersAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update user role'
      })

      // Assign campuses - update in-place
      .addCase(assignCampuses.fulfilled, (state, action) => {
        usersAdapter.upsertOne(state, action.payload)
      })
      .addCase(assignCampuses.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to assign campuses'
      })

      // Activate user - update in-place
      .addCase(activateUser.fulfilled, (state, action) => {
        usersAdapter.upsertOne(state, action.payload)
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to activate user'
      })

      // Deactivate user - update in-place
      .addCase(deactivateUser.fulfilled, (state, action) => {
        usersAdapter.upsertOne(state, action.payload)
      })
      // No state.error on a failed deactivate or removal: that is the list's
      // load error and would swap the whole table for "Couldn't load users".
      // The confirmation dialog shows the reason instead.

      // Remove member - drop from the list
      .addCase(removeUser.fulfilled, (state, action) => {
        usersAdapter.removeOne(state, action.payload)
      })

      // Fetch pending invitations
      .addCase(fetchPendingInvitations.pending, (state) => {
        state.pendingInvitationsLoading = true
      })
      .addCase(fetchPendingInvitations.fulfilled, (state, action) => {
        state.pendingInvitationsLoading = false
        state.pendingInvitations = action.payload
      })
      .addCase(fetchPendingInvitations.rejected, (state) => {
        state.pendingInvitationsLoading = false
        state.pendingInvitations = []
      })

      // Cancel invitation
      .addCase(cancelInvitation.fulfilled, (state, action) => {
        state.pendingInvitations = state.pendingInvitations.filter(
          inv => inv.id !== action.payload
        )
      })
  },
})

export const { setFilters, clearFilters, setPage, clearError, upsertMany } = usersSlice.actions

export const {
  selectAll: selectAllUsers,
  selectById: selectUserById
} = usersAdapter.getSelectors((state) => state.users || usersAdapter.getInitialState())

// Selectors for pending invitations
export const selectPendingInvitations = (state) => state.users?.pendingInvitations || []
export const selectPendingInvitationsLoading = (state) => state.users?.pendingInvitationsLoading || false

export default usersSlice.reducer
